"use client";

import React, { useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

interface BrandInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "email" | "password" | "tel" | "date";
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  maxLength?: number;
  /** Strip everything but digits while typing. */
  numericOnly?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  min?: string;
  max?: string;
}

/** Underlined input with an Integral CF label. Part of the 2027 design system. */
const BrandInput: React.FC<BrandInputProps> = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  required,
  error,
  hint,
  maxLength = 120,
  numericOnly,
  inputMode,
  min,
  max,
}) => {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const isPassword = type === "password";

  return (
    <div>
      <label htmlFor={id} className="mes-label flex justify-between text-brand-ink opacity-70">
        <span>{label}</span>
        {hint && <span className="opacity-60">{hint}</span>}
      </label>
      <div className="relative mt-2">
        <input
          id={id}
          type={isPassword && visible ? "text" : type}
          value={value}
          onChange={(e) => onChange(numericOnly ? e.target.value.replace(/\D/g, "") : e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          maxLength={maxLength}
          inputMode={inputMode ?? (numericOnly ? "numeric" : undefined)}
          min={min}
          max={max}
          aria-invalid={!!error}
          className={cn(
            "w-full bg-transparent text-brand-ink font-body text-base px-0 py-3 border-0 border-b outline-none transition-colors rounded-none",
            "placeholder:text-brand-ink/30 focus:border-brand-blue",
            "[&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer",
            type === "date" && value === "" && "text-brand-ink/40",
            isPassword && "pr-10",
            error ? "border-brand-red" : "border-brand-ink/40"
          )}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
            className="absolute inset-y-0 right-0 px-1 text-brand-ink/50 hover:text-brand-ink"
          >
            {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </div>
      {error && <p className="mes-label text-brand-red mt-2 normal-case tracking-normal">{error}</p>}
    </div>
  );
};

export default BrandInput;
